import React, { ReactElement, useState } from 'react';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Session, SessionTemplate } from '../../../../../types/entity.types';

import { convertDateToTimeString, getClassNames } from '../../../../../utils/util';
import { ARCHIVED, SHORT_MONTHS } from '../../../../../utils/constants';

import Button from '../../../../common/button/Button';
import AccountabilityAttendanceModal from './accountability-attendance-modal/AccountabilityAttendanceModal';

import { ReactComponent as DeleteIcon } from '../../../../../resources/icons/close.svg';

import './accountability-attendance.scss';
import '../accountability.scss';

type Props = {
  status: string;
  sessions: Session[];
  updateSession: (session: Partial<Session>) => void;
  deleteSession: (externalReference: string) => void;
  generateSessions: (sessionTemplate: SessionTemplate, onSuccess: () => void) => void;
};

const AccountabilityAttendance = ({
  status,
  sessions = [],
  updateSession,
  deleteSession,
  generateSessions
}: Props): ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<Session | undefined>(undefined);

  const handleAddClick = (): void => {
    setSelectedSession(undefined);
    setShowModal(true);
  };

  const handleSessionClick = (session: Session): void => {
    setSelectedSession(session);
    setShowModal(true);
  };

  const handleDeleteClick = (event: Event, session: Session): void => {
    event.stopPropagation();

    deleteSession(session.externalReference as string);
  };

  const formatDate = (session: Session): string => {
    if (!session.startDate) return '-';

    const date: Date = new Date(session.startDate);

    const day: number = date.getUTCDate();
    const month: string = SHORT_MONTHS[date.getUTCMonth()] as string;
    const year: number = date.getUTCFullYear();
    const startTime: string = convertDateToTimeString(session.startTime);
    const endTime: string = convertDateToTimeString(session.endTime);

    return `${day} ${month} ${year} ${startTime} - ${endTime}`;
  };

  const renderSessions = (): ReactElement[] => {
    const copyOfSessions: Session[] = [...sessions];
    const sortedSessions: Session[] = copyOfSessions.sort((sessionOne: Session, sessionTwo: Session): number => {
      const dateOne: Date = sessionOne.startDate;
      const dateTwo: Date = sessionTwo.startDate;

      if (dateOne > dateTwo) return 1;
      if (dateOne < dateTwo) return -1;
      return 0;
    });

    return sortedSessions.map(
      (session: Session, index: number): ReactElement => (
        <div
          className={getClassNames('accountability-attendance__session', { disabled: status === ARCHIVED })}
          key={index}
          onClick={(): void => handleSessionClick(session)}
          data-testid="session"
        >
          <div>
            <p>{session.name}</p>
          </div>
          <div>
            <p>{formatDate(session)}</p>
          </div>
          {status !== ARCHIVED && (
            <DeleteIcon
              className="accountability-attendance__delete-icon"
              onClick={(event: Event): void => handleDeleteClick(event, session)}
              data-testid="delete-icon"
            />
          )}
        </div>
      )
    );
  };

  return (
    <div
      className={getClassNames('accountability__section', {
        closed: !open
      })}
      data-testid="accountability-attendance"
    >
      <div className="accountability__section__header">
        <p>ATTENDANCE</p>
        {!open && (
          <FontAwesomeIcon
            className="accountability__section__open-icon"
            icon={faPlus}
            onClick={(): void => setOpen(true)}
            data-testid="open-icon"
          />
        )}
        {open && (
          <FontAwesomeIcon
            className="accountability__section__close-icon"
            icon={faMinus}
            onClick={(): void => setOpen(false)}
            data-testid="close-icon"
          />
        )}
      </div>
      {open && (
        <div className="accountability-attendance">
          {sessions.length > 0 && <p>Session Name</p>}
          {renderSessions()}
          {status !== ARCHIVED && <Button text="Add" onClick={handleAddClick} />}
        </div>
      )}

      <AccountabilityAttendanceModal
        status={status}
        show={showModal}
        setShow={setShowModal}
        session={selectedSession}
        updateSession={updateSession}
        generateSessions={generateSessions}
      />
    </div>
  );
};

export default AccountabilityAttendance;
